// ─────────────────────────────────────────────────────────────────────────────
// stylus_sidebar.ts — Permanent vertical stylus palette (Batch 7)
//
// Replaces the floating stylus menu pop-up with a thin column pinned
// to the page's left margin. Always available (toggleable) so the
// user can swap tool / colour / thickness / etc. without invoking a
// transient pop-up. The same menu-invocation routes (cadence pattern,
// side-button shortcut) now toggle this sidebar's visibility instead
// of opening a pop-up.
//
// The sidebar is render-only here — hit testing is owned by the
// canvas pointer handlers, which import the same `SidebarHitItem`
// shape via `getHitItems()` and dispatch the matching actions.
// Selection visuals: a thin parchment-tinted rectangle outlines the
// currently active item (per group), instead of darkening the icon.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';
import { TOOL_DEFS, INK_COLORS, THICKNESS_OPTIONS } from './drawn_ui';

export type SidebarGroup =
  | 'tool'
  | 'color'
  | 'thickness'
  | 'opacity'
  | 'shape'
  | 'autoLatex'
  | 'visualFx'
  | 'lockFocus';

export interface SidebarItem {
  group: SidebarGroup;
  /** Group-local index of this item (used for multi-option groups). */
  optionIndex: number;
  /** The value the canvas dispatches when the item is tapped. */
  value: string | number;
  /** Display payload — what the renderer paints in this slot. */
  display: 'tool-icon' | 'color-swatch' | 'thickness-swatch' | 'glyph';
  glyph?: string;        // glyph display only
  swatchColor?: string;  // color swatches
  swatchRadius?: number; // thickness swatches
  /** Tooltip / a11y label (rendered as a tiny label below the icon
   *  for the few groups where it's helpful). */
  label?: string;
}

export interface SidebarHitItem extends SidebarItem {
  /** Canvas-absolute hit rect. */
  cx: number;
  cy: number;
  r: number;   // half-cell, click radius
  /** True when this item is the currently active one for its group. */
  active: boolean;
}

const SIDEBAR_WIDTH = 44;
const CELL_H        = 22;
const CELL_W        = 22;
const SECTION_GAP   = 8;
const DIVIDER_COLOR = 'rgba(120, 90, 60, 0.35)';
const ICON_COLOR    = '#3a2010';
const SELECTION_BOX = '#3a2010';

/** Build the full vertical layout of items. Pure function — caller
 *  passes the active values per group; output drives both render
 *  and hit testing. */
export function buildSidebarLayout(
  L: PageLayout,
  active: {
    tool: string;
    color: string;
    size: number;
    opacity: number;
    shape: string;
    autoLatex: boolean;
    visualFx: boolean;
    lockFocus: boolean;
  },
): { items: SidebarHitItem[]; bgRect: { x: number; y: number; w: number; h: number } } {
  const items: SidebarHitItem[] = [];

  const x0 = L.x + 6;
  const colA = x0 + CELL_W / 2;
  const colB = x0 + CELL_W * 1.5;
  // Build with a relative origin (y starts at 0 for the first row's
  // centre); the totalH is computed at the end and the whole stack is
  // shifted down so the sidebar sits centred on the page vertically.
  let y = CELL_H / 2;

  const push = (it: Omit<SidebarHitItem, 'cx' | 'cy' | 'r' | 'active'> & { col: 0 | 1 }) => {
    const cx = it.col === 0 ? colA : colB;
    const isActive = isItemActive(it, active);
    items.push({
      ...it,
      cx,
      cy: y,
      r: CELL_W / 2 - 1,
      active: isActive,
    });
  };

  // ── Section: TOOLS (2 cols) ───────────────────────────────────────
  for (let i = 0; i < TOOL_DEFS.length; i++) {
    const t = TOOL_DEFS[i];
    const col = (i % 2) as 0 | 1;
    push({
      group: 'tool', optionIndex: i, value: t.id,
      display: 'tool-icon', glyph: t.icon, label: t.label, col,
    });
    if (col === 1 || i === TOOL_DEFS.length - 1) y += CELL_H;
  }

  y += SECTION_GAP;

  // ── Section: COLORS (2 cols) ──────────────────────────────────────
  for (let i = 0; i < INK_COLORS.length; i++) {
    const c = INK_COLORS[i];
    const col = (i % 2) as 0 | 1;
    push({
      group: 'color', optionIndex: i, value: c,
      display: 'color-swatch', swatchColor: c, col,
    });
    if (col === 1 || i === INK_COLORS.length - 1) y += CELL_H;
  }

  y += SECTION_GAP;

  // ── Section: THICKNESS (1 col) ────────────────────────────────────
  for (let i = 0; i < THICKNESS_OPTIONS.length; i++) {
    const t = THICKNESS_OPTIONS[i];
    push({
      group: 'thickness', optionIndex: i, value: t.size,
      display: 'thickness-swatch', swatchRadius: t.radius,
      col: 0,  // single-column section: rendered between the two cols
    });
    y += CELL_H;
  }
  // single-column: items use the centre between columns
  for (let i = items.length - THICKNESS_OPTIONS.length; i < items.length; i++) {
    items[i].cx = (colA + colB) / 2;
  }

  y += SECTION_GAP;

  // ── Section: OPACITY (1 col) ──────────────────────────────────────
  const OPACITIES: Array<{ v: number; g: string }> = [
    { v: 0.25, g: '◔' },
    { v: 0.50, g: '◑' },
    { v: 0.75, g: '◕' },
    { v: 1.00, g: '●' },
  ];
  for (let i = 0; i < OPACITIES.length; i++) {
    const o = OPACITIES[i];
    push({
      group: 'opacity', optionIndex: i, value: o.v,
      display: 'glyph', glyph: o.g, col: 0,
    });
    y += CELL_H;
  }
  for (let i = items.length - OPACITIES.length; i < items.length; i++) {
    items[i].cx = (colA + colB) / 2;
  }

  y += SECTION_GAP;

  // ── Section: SHAPE MODE (1 col) ───────────────────────────────────
  const SHAPES: Array<{ v: string; g: string; lab: string }> = [
    { v: 'off',  g: '⊘', lab: 'off' },
    { v: 'hint', g: '◎', lab: 'hint' },
    { v: 'full', g: '●', lab: 'full' },
  ];
  for (let i = 0; i < SHAPES.length; i++) {
    const s = SHAPES[i];
    push({
      group: 'shape', optionIndex: i, value: s.v,
      display: 'glyph', glyph: s.g, label: s.lab, col: 0,
    });
    y += CELL_H;
  }
  for (let i = items.length - SHAPES.length; i < items.length; i++) {
    items[i].cx = (colA + colB) / 2;
  }

  y += SECTION_GAP;

  // ── Section: TOGGLES (T = autoLatex, ✦ = visualFx) ────────────────
  push({ group: 'autoLatex', optionIndex: 0, value: 'toggle',
         display: 'glyph', glyph: 'T', label: 'text', col: 0 });
  push({ group: 'visualFx', optionIndex: 0, value: 'toggle',
         display: 'glyph', glyph: '✦', label: 'fx', col: 1 });
  y += CELL_H;

  y += SECTION_GAP;

  // ── Section: LOCK FOCUS (bottom) — invert interactivity so the user
  // can edit / unlock decorations and other locked content. Lives at
  // the foot of the sidebar by design: it's a mode switch, not a
  // drawing parameter.
  push({ group: 'lockFocus', optionIndex: 0, value: 'toggle',
         display: 'glyph', glyph: '\u{1F512}', label: 'locked', col: 0 });
  y += CELL_H;
  items[items.length - 1].cx = (colA + colB) / 2;

  // Vertical centering: shift the whole stack down so it's centred
  // within the page area (rather than starting from the very top).
  // The minimum-top guard (Math.max with TOP_PAD) keeps the column
  // from poking into the tab bar on short pages.
  const totalH = y;
  const TOP_PAD = 18;
  const centerOffset = Math.max(TOP_PAD, (L.height - totalH) / 2);
  for (const it of items) it.cy += L.y + centerOffset;

  // No backplate — items render directly on parchment. bgRect stays
  // around for hit testing only (defines the column's hit window).
  return {
    items,
    bgRect: {
      x: L.x,
      y: L.y + centerOffset - 4,
      w: SIDEBAR_WIDTH,
      h: totalH + 8,
    },
  };
}

/** Is this item the currently-selected one in its group? Used both
 *  for the thin-rect outline and for the hit-item's `active` flag. */
function isItemActive(
  it: { group: SidebarGroup; value: string | number },
  active: {
    tool: string; color: string; size: number; opacity: number;
    shape: string; autoLatex: boolean; visualFx: boolean;
    lockFocus: boolean;
  },
): boolean {
  switch (it.group) {
    case 'tool':      return active.tool === it.value;
    case 'color':     return active.color === it.value;
    case 'thickness': return Math.abs(active.size - (it.value as number)) < 0.1;
    case 'opacity':   return Math.abs(active.opacity - (it.value as number)) < 0.05;
    case 'shape':     return active.shape === it.value;
    case 'autoLatex': return active.autoLatex;
    case 'visualFx':  return active.visualFx;
    case 'lockFocus': return active.lockFocus;
  }
}

/** Render the sidebar at its computed layout. The render is purely a
 *  function of the layout result — no internal state. */
export function drawStylusSidebar(
  ctx: CanvasRenderingContext2D,
  L: PageLayout,
  layout: ReturnType<typeof buildSidebarLayout>,
): void {
  const { items, bgRect } = layout;

  ctx.save();

  // Section dividers — a thin horizontal line above each item that
  // starts a new section. Renderer reconstructs section starts by
  // detecting group transitions in the item list. No backplate: the
  // sidebar sits flat on the parchment, dividers + selection
  // outlines doing the structural work.
  ctx.strokeStyle = DIVIDER_COLOR;
  ctx.lineWidth = 0.6;
  let prevGroup: SidebarGroup | null = null;
  for (const it of items) {
    if (prevGroup !== null && it.group !== prevGroup) {
      const dy = it.cy - CELL_H / 2 - 4;
      ctx.beginPath();
      ctx.moveTo(bgRect.x + 6, dy);
      ctx.lineTo(bgRect.x + bgRect.w - 6, dy);
      ctx.stroke();
    }
    prevGroup = it.group;
  }

  // Items
  for (const it of items) {
    drawSidebarItem(ctx, it);
    if (it.active) drawSelectionRect(ctx, it);
  }

  ctx.restore();
}

/** Look up which item sits under a given pointer. Caller owns the
 *  click radius — items are square cells of `CELL_W × CELL_H`. */
export function hitTestSidebar(
  layout: ReturnType<typeof buildSidebarLayout>,
  px: number, py: number,
): SidebarHitItem | null {
  const { bgRect } = layout;
  if (px < bgRect.x || px > bgRect.x + bgRect.w
      || py < bgRect.y || py > bgRect.y + bgRect.h) {
    return null;
  }
  // Tightest hit: nearest-centre within the cell radius.
  let best: SidebarHitItem | null = null;
  let bestDist = Infinity;
  for (const it of layout.items) {
    const dx = it.cx - px;
    const dy = it.cy - py;
    const d  = Math.sqrt(dx * dx + dy * dy);
    if (d <= CELL_W / 2 && d < bestDist) { best = it; bestDist = d; }
  }
  return best;
}

// ── Internals ────────────────────────────────────────────────────────────────

function drawSidebarItem(
  ctx: CanvasRenderingContext2D,
  it: SidebarHitItem,
): void {
  const cx = it.cx;
  const cy = it.cy;
  ctx.fillStyle = ICON_COLOR;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  switch (it.display) {
    case 'tool-icon':
    case 'glyph': {
      ctx.font = '14px serif';
      ctx.globalAlpha = 0.9;
      ctx.fillText(it.glyph ?? '?', cx, cy);
      break;
    }
    case 'color-swatch': {
      ctx.globalAlpha = 1;
      ctx.fillStyle = it.swatchColor ?? '#5a3820';
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, Math.PI * 2);
      ctx.fill();
      // Tiny dark border so light colours stay legible
      ctx.globalAlpha = 0.45;
      ctx.strokeStyle = '#3a2010';
      ctx.lineWidth = 0.4;
      ctx.stroke();
      break;
    }
    case 'thickness-swatch': {
      ctx.globalAlpha = 0.92;
      ctx.fillStyle = '#3a2010';
      ctx.beginPath();
      ctx.arc(cx, cy, it.swatchRadius ?? 2, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
  }

  ctx.globalAlpha = 1;
}

function drawSelectionRect(
  ctx: CanvasRenderingContext2D,
  it: SidebarHitItem,
): void {
  const cx = it.cx, cy = it.cy;
  const half = it.r + 1;
  ctx.save();
  ctx.strokeStyle = SELECTION_BOX;
  ctx.lineWidth = 0.9;
  ctx.globalAlpha = 0.9;
  ctx.strokeRect(cx - half, cy - half, half * 2, half * 2);
  ctx.restore();
}

export const STYLUS_SIDEBAR_WIDTH = SIDEBAR_WIDTH;
